import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faShoppingCart, 
  faUser, 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt,
  faCommentDots,
  faTruck,
  faStore,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../contexts/CartContext';

const OrderCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  
  // Get cart data from navigation state
  const cartItems = location.state?.cartItems || [];
  
  // Redirect if no cart items and scroll to top
  React.useEffect(() => {
    // Always scroll to top when component mounts
    window.scrollTo(0, 0);
    
    if (!cartItems || cartItems.length === 0) {
      navigate('/menu');
    }
  }, [cartItems, navigate]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    deliveryType: 'delivery', // 'delivery' or 'pickup'
    comments: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryFee = 0; // Delivery fee removed - will be coordinated separately
  const total = subtotal; // Total = subtotal only (no delivery fee)

  // Handle form changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Formato de teléfono inválido';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    if (formData.deliveryType === 'delivery' && !formData.address.trim()) {
      newErrors.address = 'La dirección es obligatoria para entrega a domicilio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit order
  const handleSubmitOrder = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare order data for backend
      const orderData = {
        items: cartItems.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer: {
          name: formData.name,
          phone: formData.phone.startsWith('+') ? formData.phone : `+${formData.phone}`,
          email: formData.email || undefined,
          address: formData.deliveryType === 'delivery' ? formData.address : 'Recoger en restaurante'
        },
        comments: formData.comments || ''
      };

      console.log('Enviando orden:', orderData);
      console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/website-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response result:', result);

      if (response.ok && result.success) {
        console.log('Orden exitosa, limpiando carrito y navegando...');
        // Clear cart after successful order
        clearCart();
        
        // Navigate to success page
        navigate('/order-success', {
          state: {
            orderId: result.order_id,
            total: result.total,
            customerName: formData.name,
            deliveryType: formData.deliveryType
          }
        });
      } else {
        console.error('Orden falló:', result);
        throw new Error(result.message || 'Error al enviar la orden');
      }

    } catch (error) {
      console.error('Error enviando orden:', error);
      alert('Error al enviar la orden. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!cartItems || cartItems.length === 0) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-16">
      <div className="container-custom max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-20">
          <button
            onClick={() => navigate('/menu')}
            className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors duration-300"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            <span className="font-sans text-lg">Volver al Menú</span>
          </button>
          
          <h1 className="font-sans text-4xl md:text-5xl font-light text-gray-900 text-center">
            Finalizar <span className="font-normal italic text-green-600">Pedido</span>
          </h1>
          
          <div className="w-32"></div> {/* Spacer for centering */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Order Summary */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
              <h2 className="font-sans text-2xl md:text-3xl font-light text-gray-900 mb-6 flex items-center">
                <FontAwesomeIcon icon={faShoppingCart} className="w-6 h-6 mr-3 text-green-600" />
                Resumen del Pedido
              </h2>
              
              <div className="space-y-3 mb-6">
                {cartItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={item.image || item.images?.[0] || "https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/default-dish.jpg"} 
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-xl shadow-md flex-shrink-0"
                        onError={(e) => {
                          e.target.src = "https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/default-dish.jpg";
                        }}
                      />
                      <div>
                        <h4 className="font-medium">{item.name}</h4>
                        <p className="text-sm text-gray-600">x{item.quantity}</p>
                      </div>
                    </div>
                    <span className="font-semibold">${(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>${subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-green-600 border-t border-gray-200 pt-2">
                  <span>Total:</span>
                  <span>${total.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <h2 className="font-sans text-2xl md:text-3xl font-light text-gray-900 mb-8">
                Información de <span className="font-normal italic text-green-600">Contacto</span>
              </h2>
              
              <form className="space-y-4">
                
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-2" />
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full p-3 rounded-lg border-2 transition-colors ${
                      errors.name ? 'border-red-500' : 'border-gray-300 focus:border-green-600'
                    }`}
                    placeholder="Tu nombre completo"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <FontAwesomeIcon icon={faPhone} className="w-4 h-4 mr-2" />
                    Teléfono WhatsApp *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full p-3 rounded-lg border-2 transition-colors ${
                      errors.phone ? 'border-red-500' : 'border-gray-300 focus:border-green-600'
                    }`}
                    placeholder="+53 xxx xxxx"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-2" />
                    Email (Opcional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full p-3 rounded-lg border-2 transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300 focus:border-green-600'
                    }`}
                    placeholder="tu@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                {/* Delivery Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Tipo de Entrega</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({...prev, deliveryType: 'delivery'}))}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        formData.deliveryType === 'delivery'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <FontAwesomeIcon icon={faTruck} className="w-5 h-5 mb-2" />
                      <div className="text-sm font-medium">Entrega</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({...prev, deliveryType: 'pickup'}))}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        formData.deliveryType === 'pickup'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <FontAwesomeIcon icon={faStore} className="w-5 h-5 mb-2" />
                      <div className="text-sm font-medium">Recoger</div>
                    </button>
                  </div>
                </div>

                {/* Address (only if delivery) */}
                {formData.deliveryType === 'delivery' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4 mr-2" />
                      Dirección de Entrega *
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      className={`w-full p-3 rounded-lg border-2 transition-colors ${
                        errors.address ? 'border-red-500' : 'border-gray-300 focus:border-green-600'
                      }`}
                      placeholder="Dirección completa con referencias..."
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>
                )}

                {/* Comments */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <FontAwesomeIcon icon={faCommentDots} className="w-4 h-4 mr-2" />
                    Comentarios Especiales
                  </label>
                  <textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-3 rounded-lg border-2 border-gray-300 focus:border-green-600 transition-colors"
                    placeholder="Instrucciones especiales, preferencias dietéticas, etc."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="button"
                  onClick={handleSubmitOrder}
                  disabled={isSubmitting}
                  className="w-full bg-black hover:bg-gray-800 text-white py-4 px-8 rounded-xl font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] shadow-lg"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
                      <span>Enviando Orden...</span>
                    </div>
                  ) : (
                    `Enviar Orden - $${total.toLocaleString()}`
                  )}
                </button>

                <p className="font-sans text-sm text-gray-600 text-center mt-4">
                  Al enviar tu orden, nuestro equipo te contactará via WhatsApp para confirmar y coordinar la entrega.
                </p>

                {/* Delivery Note */}
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4 rounded">
                  <p className="font-sans text-sm text-red-800">
                    <strong>Nota:</strong> El servicio de entrega solo está disponible en La Habana
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCheckout;